import styles from "./HeroPage.module.scss";
import Header from "../../components/header/Header";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../constants/routes";
import { useState } from "react";
import useWindowSize from "../../hooks/useWindowSize";
import LogoIcon from "../../components/icons/Logo";
import swipeIcon from "./assets/swipe.svg";
import likeIcon from "./assets/like.svg";
import shareIcon from "./assets/share.svg";
import InfoIcon from "../../components/icons/InfoIcon";
import { useDispatch } from "react-redux";
import { cleanUpFeedPage } from "../../store/slices/feed.slice";
import { setFilterChanged, setGenres, setQuickFilter, setTemporaryCounter } from "../../store/slices/filters.slice";
import { useAppSelector } from "../../store/store";
import GradientBackdrop from "../../components/shared/GradientBackdrop/GradientBackdrop";

const WELCOME_GENRES = [
  { id: 28, name: "Action" },
  { id: 36, name: "Biography" },
  { id: 10749, name: "Romance" },
  { id: 878, name: "Science Fiction" },
  { id: 35, name: "Comedy" },
  { id: 53, name: "Thriller" },
  { id: 37, name: "Western" },
  { id: 80, name: "Crime" },
  { id: 12, name: "Adventure" },
  { id: 10752, name: "War" },
  { id: 9648, name: "Detective" },
  { id: 16, name: "Animation" },
  { id: 27, name: "Detective" },
  { id: 14, name: "Fantasy" },
  { id: 18, name: "Drama" },
  { id: 10751, name: "Family" },
];

const HeroPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { windowSize } = useWindowSize();
  const dispatch = useDispatch();
  const maxYear = useAppSelector((state: any) => state.filters.maxYear);
  const [activeFeature, setActiveFeature] = useState<number | null>(null);
  const [step, setStep] = useState(1);
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [hoveredGenre, setHoveredGenre] = useState<number | null>(null);
  const isMobile = windowSize === "mobile";

  const startHandler = () => {
    localStorage.setItem("heroSeen", "1");
    dispatch(cleanUpFeedPage());
    dispatch(setGenres(selectedGenres));
    dispatch(setFilterChanged(selectedGenres.length > 0));
    dispatch(setTemporaryCounter({ genres: selectedGenres, period: [1980, maxYear] }));
    dispatch(setQuickFilter(selectedGenres.length ? "popular" : "all"));
    navigate(ROUTES.FOR_YOU);
  };

  const continueHandler = () => {
    setStep(2);
    setActiveFeature(null);
  };

  const toggleGenre = (genreId: number) => {
    setSelectedGenres((currentGenres) =>
      currentGenres.includes(genreId)
        ? currentGenres.filter((currentGenreId) => currentGenreId !== genreId)
        : [...currentGenres, genreId],
    );
  };

  const features = [
    {
      label: t("hero_swipe"),
      desc: t("hero_swipe_desc"),
      icon: swipeIcon,
      iconClassName: styles.swipeIcon,
      baseClassName: styles.swipeBase,
      accentClassName: styles.swipeAccent,
    },
    {
      label: t("hero_save"),
      desc: t("hero_save_desc"),
      icon: likeIcon,
      iconClassName: styles.saveIcon,
      baseClassName: styles.saveBase,
      accentClassName: styles.saveAccent,
    },
    {
      label: t("hero_share"),
      desc: t("hero_share_desc"),
      icon: shareIcon,
      iconClassName: styles.shareIcon,
      baseClassName: styles.shareBase,
      accentClassName: styles.shareAccent,
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Header source="hero" className={styles.header} />

      <GradientBackdrop/>

      <div
        className={`${styles.content} ${
          step === 1 ? styles.contentStepOne : styles.contentStepTwo
        }`}
      >
        <div className={styles.logo}>
          <LogoIcon width={130} height={29} />
        </div>

        {step === 1 ? (
          <>
            <h1 className={styles.title}>{t("hero_title")}</h1>

            <div className={styles.features}>
              {features.map((f, i) => (
                <div
                  key={i}
                  className={`${styles.featureItem} ${activeFeature === i ? styles.featureItemActive : ""}`}
                  {...(isMobile
                    ? { onClick: () => setActiveFeature(activeFeature === i ? null : i) }
                    : { onMouseEnter: () => setActiveFeature(i), onMouseLeave: () => setActiveFeature(null) })}
                >
                  <div className={styles.iconWrapper}>
                    <div className={styles.iconInner}>
                      <img
                        src={f.icon}
                        alt=""
                        className={`${styles.iconLayer} ${styles.iconBase} ${f.iconClassName} ${f.baseClassName}`}
                      />
                      <img
                        src={f.icon}
                        alt=""
                        className={`${styles.iconLayer} ${styles.iconAccent} ${f.iconClassName} ${f.accentClassName}`}
                      />
                    </div>
                  </div>
                  <span className={styles.featureLabel}>{f.label}</span>
                </div>
              ))}
            </div>

            {
              !isMobile ? (
                  <div className={styles.clue}>
                  <span className={styles.infoIcon}>
                    <InfoIcon/>
                  </span>
                    <span>{activeFeature !== null ? features[activeFeature].desc : t("hero_swipe_desc")}</span>
                  </div>
                )
                : ''
            }

            <button className={styles.cta} onClick={continueHandler}>
              {t("hero_continue")}
            </button>
          </>
        ) : (
          <>
            <h1 className={styles.title}>{t("hero_genres_title")}</h1>

            <div className={styles.genreCloud}>
              {WELCOME_GENRES.map((genre, index) => {
                const isSelected = selectedGenres.includes(genre.id);
                const isHovered = hoveredGenre === genre.id;

                return (
                  <button
                    key={`${genre.id}_${index}`}
                    type="button"
                    className={`${styles.genreChip} ${isSelected ? styles.genreChipSelected : ""} ${
                      isHovered && !isSelected ? styles.genreChipHovered : ""
                    }`}
                    onClick={() => toggleGenre(genre.id)}
                    onMouseEnter={() => setHoveredGenre(genre.id)}
                    onMouseLeave={() => setHoveredGenre(null)}
                  >
                    {t(genre.name)}
                  </button>
                );
              })}
            </div>

            <button className={styles.cta} onClick={startHandler}>
              {t("hero_cta")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default HeroPage;
